import React, { useCallback, useEffect, useState } from 'react';
import { Loader2, Plus, Save, RefreshCw, AlertTriangle, CheckCircle2, Trash2, Pencil, Eye, EyeOff, RotateCcw } from 'lucide-react';
import { Button } from './ui/button';

const API = process.env.REACT_APP_BACKEND_URL;

const ALL_PERMISSIONS = [
  { key: 'ligne',         label: 'Vue Ligne' },
  { key: 'quota',         label: 'Chiffres mobilité' },
  { key: 'communication', label: 'Communication' },
  { key: 'infofluo',      label: 'InfoFluo' },
  { key: 'legal',         label: 'Pages légales' },
  { key: 'maintenance',   label: 'Maintenance' },
  { key: 'access',        label: 'Gestion accès' },
  { key: 'api',           label: 'API' },
];

const DEFAULT_PERMS = Object.fromEntries(ALL_PERMISSIONS.map(p => [p.key, false]));

/* ── Permission badge (green when active) ── */
const PermBadge = ({ label, active }) =>
  active
    ? <span className="inline-block px-1.5 py-0.5 rounded text-[10px] font-bold bg-emerald-100 text-emerald-800">{label}</span>
    : null;

/* ── Add / Edit form ── */
const UserForm = ({ initial, currentUsername, onSave, onCancel, loading }) => {
  const isEdit = !!initial;
  const [username, setUsername] = useState(initial?.username || '');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [perms, setPerms] = useState({ ...DEFAULT_PERMS, ...(initial?.permissions || {}) });

  const togglePerm = (key) => {
    if (key === 'access' && username === currentUsername && perms[key]) return;
    setPerms(p => ({ ...p, [key]: !p[key] }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave({ username, password: password || undefined, permissions: perms });
  };

  return (
    <form onSubmit={handleSubmit} className="bg-slate-50 border border-slate-200 rounded-xl p-5 space-y-4">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div>
          <label className="block text-[11px] font-bold tracking-wider text-slate-500 mb-1">NOM D'UTILISATEUR</label>
          <input
            type="text"
            value={username}
            onChange={e => setUsername(e.target.value)}
            disabled={isEdit}
            required
            className="w-full h-9 px-3 rounded-md border border-slate-300 focus:border-ter-blue focus:ring-1 focus:ring-ter-blue outline-none text-sm disabled:bg-slate-100 disabled:text-slate-500"
          />
        </div>
        <div>
          <label className="block text-[11px] font-bold tracking-wider text-slate-500 mb-1">
            MOT DE PASSE {isEdit && <span className="font-normal normal-case text-slate-400">(vide = inchangé)</span>}
          </label>
          <div className="relative">
            <input
              type={showPassword ? 'text' : 'password'}
              value={password}
              onChange={e => setPassword(e.target.value)}
              required={!isEdit}
              autoComplete="new-password"
              placeholder={isEdit ? 'Laisser vide pour ne pas modifier' : ''}
              className="w-full h-9 px-3 pr-9 rounded-md border border-slate-300 focus:border-ter-blue focus:ring-1 focus:ring-ter-blue outline-none text-sm font-mono"
            />
            <button
              type="button"
              onClick={() => setShowPassword(v => !v)}
              className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
              tabIndex={-1}
            >
              {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
            </button>
          </div>
        </div>
      </div>

      <div>
        <div className="text-[11px] font-bold tracking-wider text-slate-500 mb-2">PERMISSIONS</div>
        <div className="flex flex-wrap gap-2">
          {ALL_PERMISSIONS.map(({ key, label }) => {
            const isSelfAccess = key === 'access' && username === currentUsername;
            return (
              <label
                key={key}
                title={isSelfAccess ? 'Impossible de retirer votre propre accès admin' : undefined}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md border cursor-pointer select-none text-sm transition-colors ${
                  perms[key]
                    ? 'bg-ter-blue border-ter-blue text-white'
                    : 'bg-white border-slate-200 text-slate-600 hover:border-ter-blue'
                } ${isSelfAccess ? 'opacity-50 cursor-not-allowed' : ''}`}
              >
                <input
                  type="checkbox"
                  className="sr-only"
                  checked={!!perms[key]}
                  onChange={() => togglePerm(key)}
                  disabled={isSelfAccess}
                />
                {label}
              </label>
            );
          })}
        </div>
        {username === currentUsername && (
          <p className="text-[11px] text-amber-700 mt-1.5">
            La permission "Gestion accès" ne peut pas être retirée de votre propre compte.
          </p>
        )}
      </div>

      <div className="flex gap-2 pt-1">
        <Button type="submit" disabled={loading || !username} size="sm" className="bg-ter-blue hover:bg-ter-blue-dark text-white">
          {loading ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Save className="w-4 h-4 mr-1" />}
          {isEdit ? 'Enregistrer' : 'Créer'}
        </Button>
        <Button type="button" onClick={onCancel} disabled={loading} size="sm" variant="outline">
          Annuler
        </Button>
      </div>
    </form>
  );
};

/* ── Main tab ── */
const AccessTab = ({ password, currentUsername }) => {
  const [users, setUsers]             = useState([]);
  const [loading, setLoading]         = useState(false);
  const [saving, setSaving]           = useState(false);
  const [msg, setMsg]                 = useState(null);
  const [mode, setMode]               = useState(null);         // null | 'add' | { edit: user }
  const [confirmDelete, setConfirmDelete] = useState(null);     // username string
  const [confirmReset, setConfirmReset]   = useState(false);
  const [resetting, setResetting]         = useState(false);
  const [resetMsg, setResetMsg]           = useState(null);

  const authHeaders = { 'X-Admin-Password': password };

  const load = useCallback(async () => {
    setLoading(true); setMsg(null);
    try {
      const r = await fetch(`${API}/api/admin/users`, { headers: authHeaders });
      if (!r.ok) throw new Error((await r.json()).detail || 'Erreur serveur');
      const d = await r.json();
      setUsers(d.users || []);
    } catch (e) { setMsg({ ok: false, text: e.message }); }
    finally { setLoading(false); }
  }, [password]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { load(); }, [load]);

  const handleSave = async ({ username, password: pw, permissions }) => {
    setSaving(true); setMsg(null);
    try {
      const isEdit = !!mode?.edit;
      const url    = isEdit
        ? `${API}/api/admin/users/${encodeURIComponent(username)}`
        : `${API}/api/admin/users`;
      const body = isEdit
        ? { ...(pw ? { password: pw } : {}), permissions }
        : { username, password: pw, permissions };
      const r = await fetch(url, {
        method:  isEdit ? 'PUT' : 'POST',
        headers: { ...authHeaders, 'Content-Type': 'application/json' },
        body:    JSON.stringify(body),
      });
      if (!r.ok) throw new Error((await r.json()).detail || 'Erreur serveur');
      setMsg({ ok: true, text: isEdit ? 'Utilisateur mis à jour.' : 'Utilisateur créé.' });
      setMode(null);
      await load();
    } catch (e) { setMsg({ ok: false, text: e.message }); }
    finally { setSaving(false); }
  };

  const handleDelete = async (username) => {
    setSaving(true); setMsg(null);
    try {
      const r = await fetch(`${API}/api/admin/users/${encodeURIComponent(username)}`, {
        method:  'DELETE',
        headers: authHeaders,
      });
      if (!r.ok) throw new Error((await r.json()).detail || 'Erreur serveur');
      setMsg({ ok: true, text: `Utilisateur « ${username} » supprimé.` });
      setConfirmDelete(null);
      await load();
    } catch (e) { setMsg({ ok: false, text: e.message }); }
    finally { setSaving(false); }
  };

  const openEdit = (user) =>
    setMode(mode?.edit?.username === user.username ? null : { edit: user });

  const handleResetAttempts = async () => {
    setResetting(true); setResetMsg(null);
    try {
      const r = await fetch(`${API}/api/admin/reset-attempts`, {
        method: 'POST',
        headers: authHeaders,
      });
      if (!r.ok) throw new Error((await r.json()).detail || 'Erreur serveur');
      setResetMsg({ ok: true, text: 'Compteur de tentatives réinitialisé.' });
      setConfirmReset(false);
    } catch (e) { setResetMsg({ ok: false, text: e.message }); }
    finally { setResetting(false); }
  };

  return (
    <div className="max-w-3xl mx-auto px-4 py-8">

      {/* Header */}
      <div className="flex items-start justify-between gap-4 mb-6">
        <div>
          <h2 className="text-xl font-extrabold text-ter-blue mb-1">Gestion des accès</h2>
          <p className="text-sm text-slate-500">Comptes administrateurs et permissions par onglet.</p>
        </div>
        <div className="flex gap-2 shrink-0">
          <Button onClick={load} disabled={loading || saving} variant="outline" size="sm" title="Recharger">
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
          </Button>
          <Button
            onClick={() => { setMode('add'); setMsg(null); setConfirmDelete(null); }}
            disabled={loading || saving || mode === 'add'}
            size="sm"
            className="bg-ter-blue hover:bg-ter-blue-dark text-white"
          >
            <Plus className="w-4 h-4 mr-1" /> Ajouter un utilisateur
          </Button>
        </div>
      </div>

      {/* Feedback */}
      {msg && (
        <div className={`text-[12px] rounded px-3 py-2 flex items-center gap-1.5 mb-4 ${
          msg.ok
            ? 'bg-emerald-50 border border-emerald-200 text-emerald-800'
            : 'bg-red-50 border border-red-200 text-red-700'
        }`}>
          {msg.ok
            ? <CheckCircle2 className="w-3.5 h-3.5 shrink-0" />
            : <AlertTriangle className="w-3.5 h-3.5 shrink-0" />}
          {msg.text}
        </div>
      )}

      {/* New user form */}
      {mode === 'add' && (
        <div className="mb-5">
          <div className="text-[11px] font-bold tracking-wider text-slate-500 uppercase mb-2">Nouvel utilisateur</div>
          <UserForm
            currentUsername={currentUsername}
            onSave={handleSave}
            onCancel={() => setMode(null)}
            loading={saving}
          />
        </div>
      )}

      {/* User list */}
      <div className="space-y-3">
        {loading && users.length === 0 && (
          <div className="py-8 text-center text-slate-400 text-sm flex items-center justify-center gap-2">
            <Loader2 className="w-4 h-4 animate-spin" /> Chargement…
          </div>
        )}

        {users.map(user => (
          <div key={user.username} className="bg-white border border-slate-200 rounded-xl overflow-hidden">

            {/* Row */}
            <div className="flex items-center gap-3 px-4 py-3">
              <div className="w-9 h-9 rounded-full bg-ter-blue/10 border border-ter-blue/20 flex items-center justify-center shrink-0">
                <span className="text-sm font-bold text-ter-blue">{user.username[0].toUpperCase()}</span>
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-1.5 flex-wrap mb-1">
                  <span className="text-sm font-bold text-slate-800">{user.username}</span>
                  {user.username === currentUsername && (
                    <span className="px-1.5 py-0.5 rounded bg-ter-blue/10 text-[10px] font-bold text-ter-blue">vous</span>
                  )}
                </div>
                <div className="flex flex-wrap gap-1">
                  {ALL_PERMISSIONS.map(p => (
                    <PermBadge key={p.key} label={p.label} active={!!user.permissions?.[p.key]} />
                  ))}
                  {!ALL_PERMISSIONS.some(p => user.permissions?.[p.key]) && (
                    <span className="text-[11px] text-slate-400 italic">Aucune permission</span>
                  )}
                </div>
              </div>
              <div className="flex items-center gap-1 shrink-0">
                <button
                  onClick={() => { openEdit(user); setMsg(null); setConfirmDelete(null); }}
                  title="Modifier"
                  className={`p-1.5 rounded-md transition-colors ${
                    mode?.edit?.username === user.username
                      ? 'bg-ter-blue text-white'
                      : 'text-slate-400 hover:text-ter-blue hover:bg-slate-100'
                  }`}
                >
                  <Pencil className="w-4 h-4" />
                </button>
                {user.username !== currentUsername && (
                  <button
                    onClick={() => { setConfirmDelete(user.username); setMode(null); setMsg(null); }}
                    title="Supprimer"
                    className="p-1.5 rounded-md text-slate-400 hover:text-red-600 hover:bg-red-50 transition-colors"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                )}
              </div>
            </div>

            {/* Inline edit form */}
            {mode?.edit?.username === user.username && (
              <div className="border-t border-slate-100 px-4 py-4">
                <UserForm
                  initial={user}
                  currentUsername={currentUsername}
                  onSave={handleSave}
                  onCancel={() => setMode(null)}
                  loading={saving}
                />
              </div>
            )}

            {/* Delete confirmation */}
            {confirmDelete === user.username && (
              <div className="border-t border-red-100 bg-red-50 px-4 py-3 flex items-center gap-3">
                <AlertTriangle className="w-4 h-4 text-red-600 shrink-0" />
                <span className="text-sm text-red-800 flex-1">
                  Supprimer <strong>{user.username}</strong> ? Cette action est irréversible.
                </span>
                <Button
                  onClick={() => handleDelete(user.username)}
                  disabled={saving}
                  size="sm"
                  className="bg-red-600 hover:bg-red-700 text-white shrink-0"
                >
                  {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : 'Confirmer'}
                </Button>
                <Button
                  onClick={() => setConfirmDelete(null)}
                  disabled={saving}
                  size="sm"
                  variant="outline"
                  className="shrink-0"
                >
                  Annuler
                </Button>
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Rate limit reset */}
      <div className="mt-8 pt-6 border-t border-slate-200">
        <div className="text-[11px] font-bold tracking-wider text-slate-500 uppercase mb-3">Administration</div>

        {resetMsg && (
          <div className={`text-[12px] rounded px-3 py-2 flex items-center gap-1.5 mb-3 ${
            resetMsg.ok
              ? 'bg-emerald-50 border border-emerald-200 text-emerald-800'
              : 'bg-red-50 border border-red-200 text-red-700'
          }`}>
            {resetMsg.ok
              ? <CheckCircle2 className="w-3.5 h-3.5 shrink-0" />
              : <AlertTriangle className="w-3.5 h-3.5 shrink-0" />}
            {resetMsg.text}
          </div>
        )}

        {!confirmReset ? (
          <Button
            onClick={() => { setConfirmReset(true); setResetMsg(null); }}
            disabled={resetting}
            size="sm"
            variant="outline"
            className="text-amber-700 border-amber-300 hover:bg-amber-50"
          >
            <RotateCcw className="w-4 h-4 mr-1.5" /> Réinitialiser les tentatives de connexion
          </Button>
        ) : (
          <div className="flex items-center gap-3 bg-amber-50 border border-amber-200 rounded-xl px-4 py-3">
            <AlertTriangle className="w-4 h-4 text-amber-600 shrink-0" />
            <span className="text-sm text-amber-900 flex-1">
              Réinitialiser le compteur de tentatives pour toutes les IPs ?
            </span>
            <Button
              onClick={handleResetAttempts}
              disabled={resetting}
              size="sm"
              className="bg-amber-600 hover:bg-amber-700 text-white shrink-0"
            >
              {resetting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : 'Confirmer'}
            </Button>
            <Button
              onClick={() => setConfirmReset(false)}
              disabled={resetting}
              size="sm"
              variant="outline"
              className="shrink-0"
            >
              Annuler
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};

export default AccessTab;
